import React, { useEffect, useMemo, useRef, useState } from 'react'
import './css/Trends.css'
import { listPublishedPublications } from '../../../services/publicationsApi'
import { FeedCard, FeedCardPlaceholder } from './TrendsShared'
import usePromotionLookup from '../../../hooks/usePromotionLookup'

const FEATURED_CARD_TARGET = 6
const FALLBACK_SERVICE_DESCRIPTION = 'Oferta recientemente publicada en el marketplace'
const FALLBACK_PUBLICATION_TITLE = 'Publicación sin título'
const DESKTOP_VISIBLE_CARDS = 4
const CAROUSEL_TRANSITION_MS = 600
const AUTOPLAY_INTERVAL_MS = 4800

const getVisibleCardCount = () => {
  if (typeof window === 'undefined') {
    return DESKTOP_VISIBLE_CARDS
  }

  const width = window.innerWidth
  if (width >= 1280) return 4
  if (width >= 1024) return 3
  if (width >= 640) return 2
  return 1
}

const stripHtml = value =>
  String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const toNumber = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const numeric = Number.parseFloat(value)
    return Number.isFinite(numeric) ? numeric : null
  }
  return null
}

const clampTagline = value => {
  const safe = String(value || '').trim()
  if (!safe) return ''
  return safe.length > 140 ? `${safe.slice(0, 137).trim()}...` : safe
}

const mapPublicationToShowcase = (publication, isFeatured = false) => {
  if (!publication) return null

  const title = (publication.title || '').trim()
  const unitName = (publication.unitName || '').trim()
  const companyName = (publication.companyName || '').trim()
  const location = (publication.location || '').trim()

  const displayName = title || unitName || companyName || FALLBACK_PUBLICATION_TITLE
  const descriptionParts = [companyName, unitName].filter(Boolean)
  if (!descriptionParts.length && title) {
    descriptionParts.push(title)
  }
  const description = descriptionParts.join(' - ') || FALLBACK_SERVICE_DESCRIPTION

  const imageCandidate =
    publication.primaryImageUrl ||
    publication.bannerUrl ||
    publication.productImageUrl ||
    publication.unitPhotoUrl ||
    publication.companyHeroUrl ||
    publication.companyLogoUrl ||
    ''

  const image =
    typeof imageCandidate === 'string' && imageCandidate.trim().length > 0 ? imageCandidate.trim() : ''

  const numericId = publication.numericId || publication.id
  const companySlug = (publication.companySlug || '').trim()
  const membershipPlan = String(publication.companyMembershipPlan || '').trim().toLowerCase()
  const companyRating = toNumber(publication.companyRating)
  const companyCoverage = publication.companyCoverage || location || ''
  const companyResponseTime = publication.companyResponseTime || publication.sla || ''
  const publisherName = (publication.publisherName || '').trim()
  const contactName = (publication.contactName || '').trim()
  const ownerName = (publication.ownerName || '').trim()
  const userName = (publication.userName || '').trim()
  const rawCompanyTagline =
    publication.companyTagline || stripHtml(publication.description || '') || FALLBACK_SERVICE_DESCRIPTION
  const companyTagline = clampTagline(rawCompanyTagline)
  const companyProfileHref =
    publication.companyProfileHref || (companySlug ? `/empresa/${companySlug}` : '')
  const sanitizeProviderName = value => {
    const trimmed = typeof value === 'string' ? value.trim() : ''
    if (!trimmed) return ''
    const normalized = trimmed.toLowerCase()
    if (normalized === 'proveedor' || normalized === 'provider') {
      return ''
    }
    return trimmed
  }
  const providerName = sanitizeProviderName(publication.providerName) || sanitizeProviderName(companyName) || ''

  return {
    id: publication.id || numericId,
    nombre: displayName,
    desc: description,
    ubic: location || companyName || 'Ubicación por confirmar',
    precio: publication.price,
    dest: Boolean(isFeatured),
    img: image,
    href: numericId ? `/publicaciones/${numericId}` : '/marketplace',
    companySlug,
    companyProfileHref: companyProfileHref || '/marketplace',
    companyLogoUrl: publication.companyLogoUrl || '',
    companyHeroUrl: publication.companyHeroUrl || '',
    companyName,
    providerName,
    publisherName,
    publisher: publisherName,
    contactName,
    ownerName,
    userName,
    company: {
      id: publication.companyId || companySlug || numericId,
      slug: companySlug,
      membershipPlan: membershipPlan || 'free',
      logoUrl: publication.companyLogoUrl || '',
      heroUrl: publication.companyHeroUrl || '',
      tagline: companyTagline,
      coverage: companyCoverage,
      responseTime: companyResponseTime,
      rating: companyRating,
      profileHref: companyProfileHref || '/marketplace',
      industry: publication.companyCategory || '',
      name: companyName || displayName,
    },
  }
}

export default function Trends() {
  const [marketplaceRecords, setMarketplaceRecords] = useState(null)
  const [marketplaceServices, setMarketplaceServices] = useState([])
  const [recentFallback, setRecentFallback] = useState([])
  const [loadingServices, setLoadingServices] = useState(false)
  const { isPromoted } = usePromotionLookup()
  const [visibleCards, setVisibleCards] = useState(() => getVisibleCardCount())
  const [activeSlide, setActiveSlide] = useState(0)
  const [slideOffset, setSlideOffset] = useState(0)
  const [isTrackAnimated, setIsTrackAnimated] = useState(true)
  const trackRef = useRef(null)
  const cardRef = useRef(null)

  useEffect(() => {
    let ignore = false

    const fetchMarketplaceHighlights = async () => {
      setLoadingServices(true)
      try {
        const records = await listPublishedPublications({ limit: 24 })
        if (!ignore) {
          setMarketplaceRecords(Array.isArray(records) ? records : [])
        }
      } catch (error) {
        console.warn('No se pudieron cargar publicaciones del marketplace.', error)
        if (!ignore) {
          setMarketplaceRecords([])
          setMarketplaceServices([])
          setRecentFallback([])
        }
      } finally {
        if (!ignore) {
          setLoadingServices(false)
        }
      }
    }

    fetchMarketplaceHighlights()
    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const updateVisibleCards = () => {
      setVisibleCards(getVisibleCardCount())
    }

    updateVisibleCards()
    window.addEventListener('resize', updateVisibleCards)
    return () => {
      window.removeEventListener('resize', updateVisibleCards)
    }
  }, [])

  useEffect(() => {
    if (marketplaceRecords === null) {
      return
    }

    if (!marketplaceRecords.length) {
      setMarketplaceServices([])
      setRecentFallback([])
      return
    }

    const promotedRecords = marketplaceRecords.filter(record => isPromoted('publication', record.id))
    const hasPromoted = promotedRecords.length > 0
    const sourceForCards = hasPromoted ? promotedRecords : marketplaceRecords

    const nextServices = sourceForCards
      .slice(0, FEATURED_CARD_TARGET * 2)
      .map((record, index) => {
        const promoted = isPromoted('publication', record.id)
        return mapPublicationToShowcase(record, promoted || (!hasPromoted && index < 3))
      })
      .filter(Boolean)

    setMarketplaceServices(nextServices)

    const orderedRecent = marketplaceRecords
      .slice(0, FEATURED_CARD_TARGET * 3)
      .map(record => mapPublicationToShowcase(record, isPromoted('publication', record.id)))
      .filter(Boolean)
    setRecentFallback(orderedRecent)
  }, [marketplaceRecords, isPromoted])

  const featuredServices = useMemo(() => {
    const queue = []
    const seen = new Set()
    const pushUnique = item => {
      if (!item) return
      const key = item.id || item.nombre
      if (!key || seen.has(key)) return
      seen.add(key)
      queue.push(item)
    }

    const primarySource = marketplaceServices
    primarySource.forEach(pushUnique)

    if (queue.length < FEATURED_CARD_TARGET) {
      recentFallback.forEach(pushUnique)
    }

    return queue.slice(0, FEATURED_CARD_TARGET)
  }, [marketplaceServices, recentFallback])

  useEffect(() => {
    setActiveSlide(0)
  }, [featuredServices.length])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const measureSlideOffset = () => {
      if (!cardRef.current) {
        setSlideOffset(0)
        return
      }

      const cardWidth = cardRef.current.getBoundingClientRect().width
      let gap = 0

      if (trackRef.current) {
        const styles = window.getComputedStyle(trackRef.current)
        const parsedGap =
          parseFloat(styles.columnGap || styles.gap || styles.rowGap || '0') || 0
        gap = Number.isNaN(parsedGap) ? 0 : parsedGap
      }

      setSlideOffset(cardWidth + gap)
    }

    measureSlideOffset()
    window.addEventListener('resize', measureSlideOffset)
    return () => {
      window.removeEventListener('resize', measureSlideOffset)
    }
  }, [visibleCards, featuredServices.length])

  const showEmptyMessage = !loadingServices && !featuredServices.length

  const transportListings = featuredServices
  const slideCount = transportListings.length
  const shouldAutoplay = slideCount > visibleCards
  const slidesToRender = useMemo(() => {
    if (!slideCount) return []
    if (!shouldAutoplay) return transportListings
    const clones = transportListings.slice(0, Math.min(visibleCards, slideCount))
    return [...transportListings, ...clones]
  }, [transportListings, shouldAutoplay, visibleCards, slideCount])
  const placeholderCount = Math.max(0, visibleCards - slideCount)

  useEffect(() => {
    if (!shouldAutoplay) {
      setActiveSlide(0)
      return
    }

    if (slideOffset === 0) {
      return
    }

    const interval = setInterval(() => {
      setActiveSlide(previous => previous + 1)
    }, AUTOPLAY_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [shouldAutoplay, slideOffset])

  useEffect(() => {
    if (!shouldAutoplay || !slideCount) {
      setIsTrackAnimated(true)
      return
    }

    if (activeSlide === slideCount) {
      const timeout = setTimeout(() => {
        setIsTrackAnimated(false)
        setActiveSlide(0)
      }, CAROUSEL_TRANSITION_MS)
      return () => clearTimeout(timeout)
    }
  }, [activeSlide, shouldAutoplay, slideCount])

  useEffect(() => {
    if (isTrackAnimated) {
      return
    }
    if (typeof window === 'undefined') {
      return
    }
    const frame = window.requestAnimationFrame(() => {
      setIsTrackAnimated(true)
    })
    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [isTrackAnimated])

  let hasAttachedRef = false
  const attachFirstSlideRef = () => {
    if (hasAttachedRef) return null
    hasAttachedRef = true
    return cardRef
  }

  return (
    <section className="featured-opportunities" aria-labelledby="featured-opportunities-title">
      <div className="featured-opportunities__inner">
        <header className="featured-opportunities__header">
          <div className="featured-opportunities__titles">
            <p className="featured-opportunities__eyebrow">Transporte premium</p>
            <h2 id="featured-opportunities-title">Oportunidades destacadas</h2>
            <p className="featured-opportunities__description">
              Rutas activas, unidades especiales y tarifas de referencia listas para cotizar.
            </p>
          </div>
          <a className="featured-opportunities__link" href="/marketplace?tipo=transporte">
            Ver todo transporte →
          </a>
        </header>

        {showEmptyMessage && (
          <p className="featured-opportunities__empty" role="status">
            No encontramos publicaciones activas en este momento. Estamos actualizando la red.
          </p>
        )}

        <div className="featured-carousel">
          <div className="featured-carousel__viewport">
            <div
              className={`featured-carousel__track${
                isTrackAnimated ? '' : ' featured-carousel__track--static'
              }`}
              role="list"
              ref={trackRef}
              style={{
                transform: `translate3d(-${activeSlide * slideOffset}px, 0, 0)`,
                '--featured-carousel-visible': visibleCards,
              }}
            >
              {slidesToRender.map(service => (
                <div
                  className="featured-carousel__slide"
                  role="listitem"
                  key={service.id || service.nombre}
                  ref={attachFirstSlideRef()}
                >
                  <FeedCard service={service} />
                </div>
              ))}
              {placeholderCount > 0 &&
                Array.from({ length: placeholderCount }).map((_, index) => (
                  <div
                    className="featured-carousel__slide"
                    role="listitem"
                    key={`featured-placeholder-${index}`}
                    ref={attachFirstSlideRef()}
                  >
                    <FeedCardPlaceholder />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
